/**
 * ═══ DES RÉPONSES SAISIES SOUS UN COMPTE NE PARTENT PAS SOUS UN AUTRE (bug 8, tunnel) ═══
 *
 * ⚠️ L'ENTRÉE D'HISTORIQUE SURVIT AU CHANGEMENT DE COMPTE. Un autre onglet ouvre la session
 * de B : celui-ci se démonte (sa clé de session change, voir `auth/session.ts`), son état
 * React part avec lui, mais `history.state`, d'où les réponses du tunnel sont lues
 * (`funnel/answers.ts`), reste. Mesuré le 2026-10-20 : A coche le jeudi, arrive sur la
 * billetterie sans enregistrer, l'onglet voisin ouvre B, et la notice disait « ce qui n'avait
 * pas été enregistré ici a été abandonné » pendant que l'état gardait `days: [2141, 2142]`.
 * Continuer envoyait `PUT /days/2142/users/<B>`, puis la participation, l'action 41 et
 * l'email de confirmation, au nom de B et avec le choix d'A.
 *
 * Chaque écriture de réponses dans l'état de navigation porte donc l'EMPREINTE de la session
 * sous laquelle elles ont été données (`stamped`), et la lecture écarte ce qui porte une autre
 * empreinte (`composedUnder`, lu par `answersOf` et le garde de sortie) : pour l'écran, le
 * tunnel et la confirmation, c'est un parcours qui n'a encore rien répondu. Les entrées plus
 * anciennes (bouton Retour) le sont tout autant, ce qu'un effacement de la seule entrée
 * courante n'aurait pas garanti.
 *
 * ⚠️ UN ÉTAT SANS EMPREINTE SE LIT COMME AVANT : c'est celui des liens qui n'emportent aucune
 * réponse (`{}`, `{ from }`) et des tests. Tout ce qui écrit des réponses dans l'état passe
 * par `stamped` : `useAnswers`, `useFunnelNav`, le fil d'Ariane (`Steps`) et l'étape 1
 * (`Registration`).
 *
 * ⚠️ DANS UN FICHIER À PART de `answers.ts`, que de nombreux tests remplacent en bloc : un
 * export de plus là-bas leur aurait manqué.
 */
const SESSION = "session";
const PARTICIPATION = "ue";

/**
 * ═══ ET LA PARTICIPATION SOUS LAQUELLE ELLES L'ONT ÉTÉ (participation supprimée, 2026-10-20) ═══
 *
 * ⚠️ MESURÉ SUR LA PILE LOCALE : inscrit jusqu'à la confirmation, la participation supprimée
 * depuis la fiche contact du BO (`DELETE apix …/events/:e/users/:u`, qui efface la ligne
 * `UserEvents`), deux retours arrière ramenaient sur l'entrée de la confirmation, dont l'état
 * portait encore toutes les réponses : l'écran les réécrivait (`PUT days`, `PUT articles`,
 * `PUT users`) et RESSUSCITAIT l'inscription supprimée, sans un clic. La session n'avait pas
 * changé : son empreinte ne pouvait rien y voir.
 *
 * L'empreinte porte donc aussi l'identifiant de la participation (`UserEvents.id`) connu au
 * moment où les réponses ont été données, et la lecture écarte celles composées sous une
 * participation que le serveur ne rend plus (absente, ou une autre). Ce qui est « connu »
 * vient du serveur seul : `useParticipation` le note ici à chaque lecture
 * (`noteParticipation`).
 *
 * ⚠️ UNE EMPREINTE `null` (composée avant toute participation) SE LIT TOUJOURS. Un premier
 * parcours peut voir la participation naître en route (la société écrit la participation) :
 * rejeter ce qui avait été composé « sans participation » lui ferait perdre ses réponses. La
 * confirmation, une fois l'écriture faite, ré-estampille SA PROPRE entrée avec l'identifiant
 * écrit (`restampEntry`) : c'est elle qui réécrirait tout au retour arrière.
 *
 * ⚠️ TANT QUE LE SERVEUR N'A PAS RÉPONDU, RIEN N'EST ÉCARTÉ, comme avant : l'écriture de la
 * confirmation attend de toute façon la lecture de la participation (`loading`).
 */
const known = new Map<string, number | null>();
const listeners = new Set<() => void>();
let version = 0;

/** Ce que le serveur vient de rendre pour la participation de cette session (`null` : aucune). */
export function noteParticipation(session: string, id: number | null): void {
  if (known.has(session) && known.get(session) === id) return;
  known.set(session, id);
  version += 1;
  listeners.forEach((listener) => listener());
}

/** La participation connue sous cette session : un identifiant, `null` (aucune), `undefined` (pas encore lue). */
export function knownParticipation(session: string): number | null | undefined {
  return known.has(session) ? known.get(session) : undefined;
}

/** Pour `useSyncExternalStore` : un lecteur de réponses se relit quand la participation connue change. */
export function subscribeToParticipation(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
export const participationVersion = (): number => version;

/** Tests seulement : repartir d'une page neuve. */
export function forgetParticipations(): void {
  known.clear();
  version += 1;
}

/** L'état a-t-il été écrit sous cette session (ou sans empreinte du tout), et sous une participation qui existe encore ? */
export function composedUnder(state: unknown, session: string): boolean {
  if (!state || typeof state !== "object") return true;
  const stamp = (state as Record<string, unknown>)[SESSION];
  if (stamp !== undefined && stamp !== session) return false;
  const under = (state as Record<string, unknown>)[PARTICIPATION];
  const now = knownParticipation(session);
  return typeof under !== "number" || now === undefined || now === under;
}

/** L'état de navigation à écrire : ces réponses, la session et la participation sous lesquelles elles l'ont été. */
export function stamped<T extends object>(answers: T, session: string): T & { session: string } {
  const ue = knownParticipation(session);
  return {
    ...answers,
    [SESSION]: session,
    ...(ue !== undefined ? { [PARTICIPATION]: ue } : {}),
  } as T & { session: string };
}

/**
 * ═══ L'ENTRÉE COURANTE, RÉ-ESTAMPILLÉE APRÈS UNE ÉCRITURE ═══
 *
 * Ses réponses sont maintenant celles de la participation `id`. Directement dans
 * `window.history` (clé et rang de l'entrée gardés : la marque d'email de la confirmation
 * en dépend) : c'est ce que le navigateur rendra au rechargement et au retour arrière.
 * Seulement si l'entrée porte des réponses de CETTE session.
 */
export function restampEntry(session: string, id: number | null): void {
  try {
    const entry = window.history.state as { usr?: unknown } | null;
    const usr = entry?.usr as Record<string, unknown> | undefined;
    if (!usr || typeof usr !== "object" || usr[SESSION] !== session) return;
    window.history.replaceState({ ...entry, usr: { ...usr, [PARTICIPATION]: id } }, "");
  } catch {
    /* pas d'historique (tests, environnement sans fenêtre) : rien à ré-estampiller */
  }
}
